import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { EntrepriseRow, FormeJuridiqueDz } from '@/lib/database.types';

const KEY = ['entreprises'] as const;

export function useEntreprises() {
  return useQuery({
    queryKey: KEY,
    queryFn: async (): Promise<EntrepriseRow[]> => {
      const { data, error } = await supabase
        .from('entreprises')
        .select('*')
        .order('is_demo', { ascending: false })
        .order('nom');
      if (error) throw error;
      return data;
    },
  });
}

export function useEntreprise(id: string | null) {
  return useQuery({
    queryKey: ['entreprise', id],
    enabled: !!id,
    queryFn: async (): Promise<EntrepriseRow | null> => {
      if (!id) return null;
      const { data, error } = await supabase
        .from('entreprises')
        .select('*')
        .eq('id', id)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });
}

export interface UpsertEntrepriseInput {
  id?: string;
  groupe_id: string;
  nom: string;
  forme_juridique: FormeJuridiqueDz;
  nif?: string | null;
  rc?: string | null;
  adresse?: string | null;
  ville?: string | null;
  telephone?: string | null;
  email?: string | null;
  logo_url?: string | null;
  couleur_accent: string;
  is_demo: boolean;
  is_active?: boolean;
}

export function useUpsertEntreprise() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpsertEntrepriseInput): Promise<EntrepriseRow> => {
      const { id, ...rest } = input;
      if (id) {
        const { data, error } = await supabase
          .from('entreprises')
          .update(rest)
          .eq('id', id)
          .select('*')
          .single();
        if (error) throw error;
        return data;
      }
      const { data, error } = await supabase.from('entreprises').insert(rest).select('*').single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

export function useDeleteEntreprise() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const { error } = await supabase.from('entreprises').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

export function useGroupes() {
  return useQuery({
    queryKey: ['groupes'],
    queryFn: async () => {
      const { data, error } = await supabase.from('groupes').select('*').order('nom');
      if (error) throw error;
      return data;
    },
  });
}
